import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ROLE_LABELS, hasPermission, UserRole } from "../types/church.js";
import Icon from "./Icon";
import perfilService from "../services/perfilService";
import DevocionalDiario from "./DevocionalDiario";
import LoadingSpinner from "./LoadingSpinner";

const DashboardCard = ({ icon, color, title, description, onClick }) => {
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className="card dashboard-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <div className="card-body">
        <div
          className="dashboard-card-icon"
          style={{
            background: `var(--color-${color}-light)`,
            color: `var(--color-${color})`,
          }}
        >
          <Icon name={icon} size={20} color={`var(--color-${color})`} />
        </div>
        <h4 className="mb-1">{title}</h4>
        <p className="text-sm text-muted mb-0">{description}</p>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { currentUser, churchUser } = useAuth();
  const navigate = useNavigate();

  if (!churchUser) {
    return (
      <LoadingSpinner
        fullPage={true}
        size="lg"
        message="Carregando dados do usuário..."
      />
    );
  }

  const primeiroNome = perfilService
    .getDisplayName(currentUser, churchUser)
    .split(" ")[0];

  const hasAdminCards =
    hasPermission(churchUser.role, UserRole.DIACONO) ||
    hasPermission(churchUser.role, UserRole.SEGUNDO_SECRETARIO) ||
    hasPermission(churchUser.role, UserRole.SEGUNDO_TESOUREIRO) ||
    hasPermission(churchUser.role, UserRole.PRESBITERO) ||
    hasPermission(churchUser.role, UserRole.DIRETOR_PATRIMONIO) ||
    hasPermission(churchUser.role, UserRole.PASTOR) ||
    churchUser.role === UserRole.DESENVOLVEDOR;

  return (
    <div
      style={{ minHeight: "100vh", backgroundColor: "var(--color-background)" }}
    >
      <main className="p-4">
        {/* Devocional Diário */}
        <DevocionalDiario />

        {/* Saudação */}
        <div className="dashboard-welcome">
          <h2>Olá, {primeiroNome}!</h2>
          <span className="badge bg-primary">{ROLE_LABELS[churchUser.role]}</span>
        </div>

        {/* Acesso Rápido */}
        <section>
          <h3 className="dashboard-section-title mb-3">
            <Icon name="zap" size={18} /> Acesso Rápido
          </h3>
          <div className="dashboard-grid">
            <DashboardCard
              icon="wallet"
              color="success"
              title="Minhas Contribuições"
              description="Visualizar todas as suas contribuições"
              onClick={() => navigate("/minhas-contribuicoes")}
            />
            <DashboardCard
              icon="bar-chart"
              color="info"
              title="Relatório Financeiro"
              description="Visualizar resumo financeiro da igreja"
              onClick={() => navigate("/relatorio-aberto")}
            />
            <DashboardCard
              icon="calendar"
              color="secondary"
              title="Eventos"
              description="Visualizar eventos da igreja"
              onClick={() => navigate("/eventos")}
            />
            <DashboardCard
              icon="clock"
              color="primary"
              title="Minhas Escalas"
              description="Ver suas escalas de serviço"
              onClick={() => navigate("/minhas-escalas")}
            />
            <DashboardCard
              icon="book-open"
              color="primary"
              title="Leitura Bíblica"
              description="Ler a Bíblia em diversas versões"
              onClick={() => navigate("/biblia")}
            />
            <DashboardCard
              icon="alert-circle"
              color="warning"
              title="Irregularidades"
              description="Reportar problemas no prédio da igreja"
              onClick={() => navigate("/irregularidades")}
            />
          </div>
        </section>

        {/* Administração */}
        {hasAdminCards && (
          <>
            <div className="dashboard-divider" />
            <section>
              <h3 className="dashboard-section-title mb-3">
                <Icon name="settings" size={18} /> Administração
              </h3>
              <div className="dashboard-grid">
                {hasPermission(churchUser.role, UserRole.DIACONO) && (
                  <DashboardCard
                    icon="list"
                    color="primary"
                    title="Gerenciar Escalas"
                    description="Criar e gerenciar escalas de serviço"
                    onClick={() => navigate("/gerenciar-escalas")}
                  />
                )}
                {hasPermission(churchUser.role, UserRole.SEGUNDO_SECRETARIO) && (
                  <DashboardCard
                    icon="calendar"
                    color="info"
                    title="Gerenciar Eventos"
                    description="Criar e gerenciar eventos da igreja"
                    onClick={() => navigate("/gerenciar-eventos")}
                  />
                )}
                {[UserRole.SECRETARIO, UserRole.SEGUNDO_SECRETARIO, UserRole.PASTOR, UserRole.DESENVOLVEDOR].includes(churchUser.role) && (
                  <DashboardCard
                    icon="folder"
                    color="secondary"
                    title="Gerenciar Arquivos"
                    description="Organizar documentos e arquivos em pastas"
                    onClick={() => navigate("/gerenciar-arquivos")}
                  />
                )}
                {hasPermission(churchUser.role, UserRole.SEGUNDO_SECRETARIO) && (
                  <DashboardCard
                    icon="users"
                    color="primary"
                    title="Membros"
                    description="Gerenciar membros da igreja"
                    onClick={() => navigate("/membros")}
                  />
                )}
                {hasPermission(churchUser.role, UserRole.SEGUNDO_SECRETARIO) && (
                  <DashboardCard
                    icon="mail"
                    color="info"
                    title="Enviar Convites"
                    description="Convidar membros para acessar o sistema"
                    onClick={() => navigate("/gerenciar-convites")}
                  />
                )}
                {hasPermission(churchUser.role, UserRole.SEGUNDO_TESOUREIRO) && (
                  <DashboardCard
                    icon="dollar-sign"
                    color="success"
                    title="Financeiro"
                    description="Gerenciar saídas e relatórios financeiros"
                    onClick={() => navigate("/financeiro")}
                  />
                )}
                {hasPermission(churchUser.role, UserRole.PRESBITERO) && (
                  <DashboardCard
                    icon="heart"
                    color="success"
                    title="Gerenciar Devocionais"
                    description="Publicar e gerenciar devocionais diários"
                    onClick={() => navigate("/gerenciar-devocionais")}
                  />
                )}
                {hasPermission(churchUser.role, UserRole.DIRETOR_PATRIMONIO) && (
                  <DashboardCard
                    icon="archive"
                    color="primary"
                    title="Gerenciar Patrimônio"
                    description="Controlar bens e calcular depreciação"
                    onClick={() => navigate("/patrimonio")}
                  />
                )}
                {hasPermission(churchUser.role, UserRole.PASTOR) && (
                  <>
                    <DashboardCard
                      icon="shield"
                      color="warning"
                      title="Gerenciar Funções"
                      description="Atribuir funções aos membros da igreja"
                      onClick={() => navigate("/gerenciar-roles")}
                    />
                    <DashboardCard
                      icon="bell"
                      color="info"
                      title="Enviar Notificações"
                      description="Enviar notificações push para membros"
                      onClick={() => navigate("/send-notifications")}
                    />
                  </>
                )}
                {hasPermission(churchUser.role, UserRole.SECRETARIO) && (
                  <DashboardCard
                    icon="image"
                    color="info"
                    title="Imagens Públicas"
                    description="Gerenciar imagens do carrossel da área de visitantes"
                    onClick={() => navigate("/gerenciar-imagens-publicas")}
                  />
                )}
                {churchUser.role === UserRole.DESENVOLVEDOR && (
                  <DashboardCard
                    icon="settings"
                    color="warning"
                    title="Administração Avançada"
                    description="Gerenciar usuários e configurações do sistema"
                    onClick={() => navigate("/gerenciar-usuarios")}
                  />
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
